import React, { useEffect, useMemo, useRef } from "react";
import {
  IconArrowLeft,
  IconCheck,
  IconCornerDownRight,
  IconMaximize,
  IconMinus,
  IconPlus,
  IconRotate,
  IconSearch,
} from "@tabler/icons-react";
import { useEncoderCodeSearch } from "../../hooks/queries/useEncoder";
import type {
  CmIndexTermNode,
  CmTableTermNode,
  CmTabularNotes,
  CmTabularResult,
  CmTabularSearchHit,
  CmTabularSearchMode,
  CodeSearchResult,
} from "../../types/encoder";
import { cn } from "../../utils/encoder";
import {
  CM_ALPHABET,
  CM_DRUG_HEADERS,
  CM_NEOPLASM_HEADERS,
  CM_NOTE_SECTIONS,
  type CmDeepSearchHit,
  type ExpandState,
  displayIncompleteCmCode,
  formatCmCode,
  highlightCmMatches,
  isNodeOpen,
  notesHaveContent,
  sectionLetterOf,
  stripIncompleteSuffix,
  toDotlessCmCode,
} from "../../utils/encoder-cm";
import { CodeLabels, Spinner } from "./shared";

export const CmEmptyState: React.FC<{ title: string; hint: string }> = ({
  title,
  hint,
}) => (
  <div className="flex h-full min-h-[240px] flex-col items-center justify-center px-6 text-center">
    <p className="text-[15px] font-semibold text-slate-900">{title}</p>
    <p className="mt-1 max-w-sm text-sm text-slate-500">{hint}</p>
  </div>
);

export const HighlightedText: React.FC<{ text: string; words: string[] }> = ({
  text,
  words,
}) => {
  const segments = highlightCmMatches(text, words);
  if (segments.length === 0) return null;
  if (segments.length === 1 && !segments[0]?.match) return <>{text}</>;
  return (
    <>
      {segments.map((segment, index) =>
        segment.match ? (
          <mark
            key={`${segment.text}-${index}`}
            className="rounded-[3px] bg-yellow-100/80 px-0.5 font-semibold text-yellow-900"
          >
            {segment.text}
          </mark>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        )
      )}
    </>
  );
};

export const CodedTermSuggestion: React.FC<{
  hit: CmDeepSearchHit;
  words: string[];
  onSelect: () => void;
}> = ({ hit, words, onSelect }) => {
  const title = hit.nemod ? `${hit.title} ${hit.nemod}` : hit.title;
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-start gap-3 px-3 py-2 text-left transition-colors hover:bg-sky-100"
    >
      <span className="min-w-0 flex-1">
        {hit.path.length > 0 && (
          <span className="block text-[12px] leading-5 text-slate-500">
            {hit.path.map((step, index) => (
              <span key={`${hit.id}-step-${index}`}>
                <HighlightedText text={step} words={words} />
                <span className="mx-1 text-slate-400">&gt;</span>
              </span>
            ))}
          </span>
        )}
        <span className="block text-[13px] leading-5 text-slate-900">
          <HighlightedText text={title} words={words} />
        </span>
      </span>
      <span className="shrink-0 pt-0.5 font-mono text-[13px] font-medium text-sky-700">
        {formatCmCode(hit.code)}
      </span>
    </button>
  );
};

export const CodeBadgeButton: React.FC<{
  code: string;
  onClick: () => void;
  isActive?: boolean;
  size?: "sm" | "md";
  needsSeventhChar?: boolean;
}> = ({
  code,
  onClick,
  isActive = false,
  size = "sm",
  needsSeventhChar = false,
}) => {
  const label = needsSeventhChar
    ? displayIncompleteCmCode(code, true)
    : formatCmCode(stripIncompleteSuffix(code));
  return (
    <button
      type="button"
      onClick={onClick}
      title={
        needsSeventhChar
          ? `${label} is incomplete — open it to add the required characters`
          : `Open ${label} in Tabular Validation`
      }
      className={cn(
        "inline-flex w-fit items-center rounded-md border font-mono font-semibold transition-colors",
        size === "md" ? "h-7 px-2.5 text-[15px]" : "h-6 px-2 text-[13px]",
        needsSeventhChar
          ? "border-dashed border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
          : "border-sky-200 bg-sky-50 text-sky-950 hover:border-sky-300 hover:bg-sky-100",
        isActive &&
          "border-green-500 bg-green-100 text-gray-900 ring-2 ring-green-300 ring-offset-1 hover:border-green-500 hover:bg-green-100"
      )}
    >
      {label}
    </button>
  );
};

export const CrossRefButton: React.FC<{
  label: string;
  target: string;
  onClick: () => void;
}> = ({ label, target, onClick }) => (
  <button
    type="button"
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    }}
    title={`Go to “${target}”`}
    className="inline-flex max-w-full items-center gap-1 text-[13px] font-medium text-blue-700 underline underline-offset-2 transition-colors hover:text-blue-900"
  >
    <IconCornerDownRight size={12} className="shrink-0" />
    <span className="truncate">
      {label} {target}
    </span>
  </button>
);

const DisclosureToggle: React.FC<{ open: boolean; onToggle: () => void }> = ({
  open,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={open ? "Collapse" : "Expand"}
    className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-teal-700 bg-white text-teal-800 transition-colors hover:bg-teal-50"
  >
    {open ? <IconMinus size={10} stroke={2.5} /> : <IconPlus size={10} stroke={2.5} />}
  </button>
);

export const AlphabetJumpBar: React.FC<{
  letters: Record<string, true>;
  onJump: (letter: string) => void;
}> = ({ letters, onJump }) => (
  <div className="ml-auto flex shrink-0 flex-nowrap items-center justify-end gap-px">
    {CM_ALPHABET.map((letter) => {
      const available = Boolean(letters[letter]);
      return (
        <button
          key={letter}
          type="button"
          disabled={!available}
          onClick={() => onJump(letter)}
          title={
            available
              ? `Jump to sections starting with ${letter}`
              : `No section starts with ${letter}`
          }
          className={cn(
            "inline-flex h-7 w-5 shrink-0 items-center justify-center rounded border px-0 text-[11px] font-semibold transition-colors",
            available
              ? "border-blue-400 bg-blue-50 text-gray-700 hover:border-blue-500 hover:bg-blue-100 hover:text-gray-800"
              : "cursor-default border-blue-100 bg-blue-50/50 text-gray-300"
          )}
        >
          {letter}
        </button>
      );
    })}
  </div>
);

export const IndexTreeView: React.FC<{
  node: CmIndexTermNode;
  onCode: (code: string) => void;
  onCrossRef: (label: string, term: string) => void;
  expand: ExpandState;
  onToggle: (id: string, next: boolean) => void;
  activeCode: string | null;
  highlightWords: string[];
  seventhCharCodes: Record<string, true>;
  depth?: number;
  jumpSectionId?: string | null;
  jumpedTermId?: string | null;
}> = ({
  node,
  onCode,
  onCrossRef,
  expand,
  onToggle,
  activeCode,
  highlightWords,
  seventhCharCodes,
  depth = 0,
  jumpSectionId,
  jumpedTermId,
}) => {
  const open = isNodeOpen(expand, node.id, depth);
  const hasChildren = node.children.length > 0;
  const isActive =
    Boolean(activeCode) && stripIncompleteSuffix(node.code) === activeCode;
  const sectionLetter = depth === 1 ? sectionLetterOf(node.title) : null;
  const isLeadTerm = depth === 0;
  const isJumpTarget =
    (Boolean(jumpSectionId) && node.id === jumpSectionId) ||
    (Boolean(jumpedTermId) && node.id === jumpedTermId);
  const needsSeventh = Boolean(seventhCharCodes[toDotlessCmCode(node.code)]);

  return (
    <div>
      <div
        data-cm-tree-term={node.id}
        data-cm-section-letter={sectionLetter ?? undefined}
        className={cn(
          "flex items-start gap-2 rounded-md px-2 py-1.5 transition-colors",
          isLeadTerm
            ? "sticky top-0 z-20 rounded-none bg-white"
            : "hover:bg-slate-100/70"
        )}
      >
        {hasChildren ? (
          <DisclosureToggle
            open={open}
            onToggle={() => onToggle(node.id, !open)}
          />
        ) : (
          <span className="mt-0.5 h-4 w-4 shrink-0" />
        )}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-[15px] leading-relaxed text-slate-900">
            <span
              className={cn(
                depth === 0 ? "font-semibold" : "font-medium",
                isJumpTarget && "bg-amber-100 text-slate-900"
              )}
            >
              <HighlightedText
                text={node.title}
                words={isJumpTarget ? [] : highlightWords}
              />
            </span>
            {node.nemod ? (
              <span className="ml-1 text-slate-600">
                <HighlightedText text={node.nemod} words={highlightWords} />
              </span>
            ) : null}
          </p>
          {node.see ? (
            <CrossRefButton
              label="see"
              target={node.see}
              onClick={() => onCrossRef("see", node.see)}
            />
          ) : null}
          {node.seeAlso ? (
            <CrossRefButton
              label="see also"
              target={node.seeAlso}
              onClick={() => onCrossRef("see also", node.seeAlso)}
            />
          ) : null}
          {node.code ? (
            <CodeBadgeButton
              code={node.code}
              isActive={isActive}
              needsSeventhChar={needsSeventh}
              onClick={() => onCode(node.code)}
            />
          ) : null}
          {node.manif ? (
            <CodeBadgeButton
              code={node.manif}
              needsSeventhChar={Boolean(
                seventhCharCodes[toDotlessCmCode(node.manif)]
              )}
              onClick={() => onCode(node.manif)}
            />
          ) : null}
        </div>
      </div>
      {open && hasChildren
        ? node.children.map((child) => (
            <div key={child.id} className="ml-3 border-l border-slate-200 pl-3">
              <IndexTreeView
                node={child}
                onCode={onCode}
                onCrossRef={onCrossRef}
                expand={expand}
                onToggle={onToggle}
                activeCode={activeCode}
                highlightWords={highlightWords}
                seventhCharCodes={seventhCharCodes}
                depth={depth + 1}
                jumpSectionId={jumpSectionId}
                jumpedTermId={jumpedTermId}
              />
            </div>
          ))
        : null}
    </div>
  );
};

const TableRows: React.FC<{
  node: CmTableTermNode;
  onCode: (code: string) => void;
  onCrossRef: (label: string, term: string) => void;
  expand: ExpandState;
  onToggle: (id: string, next: boolean) => void;
  activeCode: string | null;
  highlightWords: string[];
  seventhCharCodes: Record<string, true>;
  jumpedTermId: string | null;
  depth: number;
}> = ({
  node,
  onCode,
  onCrossRef,
  expand,
  onToggle,
  activeCode,
  highlightWords,
  seventhCharCodes,
  jumpedTermId,
  depth,
}) => {
  const open = isNodeOpen(expand, node.id, depth);
  const hasChildren = node.children.length > 0;
  const isJumpTarget = jumpedTermId === node.id;
  return (
    <>
      <tr
        data-cm-table-term={node.id}
        className="scroll-mt-14 transition-colors hover:bg-slate-100/70"
      >
        <td className="border-b border-slate-200 px-2 py-1.5 align-top">
          <div
            className="flex items-start gap-1.5"
            style={{ paddingLeft: depth * 14 }}
          >
            {hasChildren ? (
              <DisclosureToggle
                open={open}
                onToggle={() => onToggle(node.id, !open)}
              />
            ) : (
              <span className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <p className="min-w-0 text-[15px] leading-relaxed text-slate-900">
                <span
                  className={cn(
                    depth === 0 ? "font-semibold" : "font-medium",
                    isJumpTarget && "bg-amber-100 text-slate-900"
                  )}
                >
                  <HighlightedText
                    text={node.title}
                    words={isJumpTarget ? [] : highlightWords}
                  />
                </span>
                {node.nemod ? (
                  <span className="ml-1 text-slate-600">{node.nemod}</span>
                ) : null}
              </p>
              {node.see ? (
                <CrossRefButton
                  label="see"
                  target={node.see}
                  onClick={() => onCrossRef("see", node.see)}
                />
              ) : null}
              {node.seeAlso ? (
                <CrossRefButton
                  label="see also"
                  target={node.seeAlso}
                  onClick={() => onCrossRef("see also", node.seeAlso)}
                />
              ) : null}
            </div>
          </div>
        </td>
        {node.cells.map((cell, index) => (
          <td
            key={`${node.id}-cell-${index}`}
            className="border-b border-slate-200 px-2 py-1.5 align-top"
          >
            {cell ? (
              <CodeBadgeButton
                code={cell}
                isActive={
                  Boolean(activeCode) &&
                  stripIncompleteSuffix(cell) === activeCode
                }
                needsSeventhChar={Boolean(
                  seventhCharCodes[toDotlessCmCode(cell)]
                )}
                onClick={() => onCode(cell)}
              />
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </td>
        ))}
      </tr>
      {open && hasChildren
        ? node.children.map((child) => (
            <TableRows
              key={child.id}
              node={child}
              onCode={onCode}
              onCrossRef={onCrossRef}
              expand={expand}
              onToggle={onToggle}
              activeCode={activeCode}
              highlightWords={highlightWords}
              seventhCharCodes={seventhCharCodes}
              jumpedTermId={jumpedTermId}
              depth={depth + 1}
            />
          ))
        : null}
    </>
  );
};

export const TableTreeView: React.FC<{
  trees: CmTableTermNode[];
  source: "drugs" | "neoplasm";
  onCode: (code: string) => void;
  onCrossRef: (label: string, term: string) => void;
  expand: ExpandState;
  onToggle: (id: string, next: boolean) => void;
  activeCode: string | null;
  highlightWords: string[];
  seventhCharCodes: Record<string, true>;
  jumpedTermId: string | null;
  footnote?: string | null;
}> = ({
  trees,
  source,
  onCode,
  onCrossRef,
  expand,
  onToggle,
  activeCode,
  highlightWords,
  seventhCharCodes,
  jumpedTermId,
  footnote,
}) => {
  const headers = source === "drugs" ? CM_DRUG_HEADERS : CM_NEOPLASM_HEADERS;
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <table className="min-w-full border-separate border-spacing-0 text-left">
        <thead className="sticky top-0 z-10 bg-slate-50">
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                className="border-b border-slate-200 px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trees.map((node) => (
            <TableRows
              key={node.id}
              node={node}
              onCode={onCode}
              onCrossRef={onCrossRef}
              expand={expand}
              onToggle={onToggle}
              activeCode={activeCode}
              highlightWords={highlightWords}
              seventhCharCodes={seventhCharCodes}
              jumpedTermId={jumpedTermId}
              depth={0}
            />
          ))}
        </tbody>
      </table>
      {footnote ? (
        <p className="px-3 py-2 text-xs text-slate-500">{footnote}</p>
      ) : null}
    </div>
  );
};

const NOTE_TONE_STYLES: Record<
  "hard" | "soft" | "prompt" | "plain",
  { dot: string; label: string; block: string; bullet: string }
> = {
  hard: {
    dot: "bg-red-500",
    label: "text-red-900",
    block: "border-red-200 bg-red-50",
    bullet: "text-red-500",
  },
  soft: {
    dot: "bg-amber-500",
    label: "text-amber-900",
    block: "border-amber-200 bg-amber-50",
    bullet: "text-amber-500",
  },
  prompt: {
    dot: "bg-sky-500",
    label: "text-sky-900",
    block: "border-sky-200 bg-sky-50",
    bullet: "text-sky-500",
  },
  plain: {
    dot: "bg-slate-500",
    label: "text-slate-900",
    block: "border-slate-300 bg-white shadow-sm",
    bullet: "text-slate-500",
  },
};

export const NoteGroups: React.FC<{ notes: CmTabularNotes }> = ({ notes }) => {
  const sections = CM_NOTE_SECTIONS.filter(
    (section) => notes[section.key].length > 0
  );
  if (sections.length === 0) return null;
  return (
    <div className="space-y-2.5">
      {sections.map((section) => {
        const tone = NOTE_TONE_STYLES[section.tone];
        return (
          <div key={section.key} className="p-1">
            <div className="mb-1 flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cn("h-1.5 w-1.5 rounded-full", tone.dot)}
              />
              <h5
                className={cn(
                  "text-sm font-semibold uppercase tracking-wide",
                  tone.label
                )}
              >
                {section.title}
              </h5>
            </div>
            <ul className={cn("space-y-1 rounded-md border p-1.5", tone.block)}>
              {notes[section.key].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-base leading-relaxed break-words text-slate-800"
                >
                  <span aria-hidden="true" className={cn("mt-px", tone.bullet)}>
                    •
                  </span>
                  <span className="min-w-0">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const StatusChip: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex h-5 items-center rounded border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
    {label}
  </span>
);

const ValidCodeLabelBadges: React.FC<{ code: string; year: number }> = ({
  code,
  year,
}) => {
  const lookup = toDotlessCmCode(code);
  const searchQuery = formatCmCode(stripIncompleteSuffix(code));
  const { data } = useEncoderCodeSearch(searchQuery || lookup, "Dx", year, {
    enabled: lookup.length >= 3,
    limit: 20,
  });
  const hit = useMemo(() => {
    if (!lookup || !data?.length) return null;
    return (
      data.find((row: CodeSearchResult) => toDotlessCmCode(row.code) === lookup) ??
      data.find(
        (row: CodeSearchResult) =>
          row.code.replace(/\./g, "").toUpperCase() === lookup
      ) ??
      null
    );
  }, [data, lookup]);
  if (!hit) return null;
  return <CodeLabels result={hit} />;
};

const CodeRow: React.FC<{
  code: string;
  description: string;
  note?: string;
  onOpen?: () => void;
}> = ({ code, description, note, onOpen }) => (
  <li className="grid grid-cols-[104px_1fr] items-start gap-3 bg-white px-3 py-2">
    {onOpen ? (
      <CodeBadgeButton code={code} size="md" onClick={onOpen} />
    ) : (
      <span className="inline-flex h-7 w-fit items-center rounded-md border border-sky-200 bg-sky-50 px-2.5 font-mono text-[15px] font-bold text-sky-950">
        {formatCmCode(code)}
      </span>
    )}
    <span className="min-w-0 text-[13px] leading-relaxed break-words text-slate-700">
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span>{description}</span>
        {note ? <span className="text-xs text-slate-500">({note})</span> : null}
      </span>
    </span>
  </li>
);

export const TabularListSearch: React.FC<{
  mode: CmTabularSearchMode;
  onModeChange: (mode: CmTabularSearchMode) => void;
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  results: CmTabularSearchHit[];
  loading: boolean;
  activeCode: string | null;
  minLength: number;
  onSelect: (code: string) => void;
  onReset: () => void;
}> = ({
  mode,
  onModeChange,
  value,
  onValueChange,
  open,
  onOpenChange,
  results,
  loading,
  activeCode,
  minLength,
  onSelect,
  onReset,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const placeholder =
    mode === "code"
      ? "Search by code (e.g. D49, E11.9, S72)…"
      : "Search by description (e.g. neoplasm, diabetes)…";

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [open, onOpenChange]);

  return (
    <div ref={rootRef} className="relative z-20 shrink-0 border-b">
      <header className="flex h-10 items-center gap-2 px-3">
        <h3 className="shrink-0 text-base font-semibold text-green-700">
          Tabular Validation
        </h3>
        <div className="flex h-8 shrink-0 items-center gap-0.5 rounded-md border bg-slate-100 p-0.5">
          {([
            { value: "code" as const, label: "Code" },
            { value: "text" as const, label: "Text" },
          ]).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onModeChange(opt.value)}
              className={cn(
                "inline-flex h-7 items-center rounded-[5px] px-2.5 text-sm font-medium transition-colors",
                mode === opt.value
                  ? "bg-slate-800 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-200/70 hover:text-slate-900"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-0 flex-1">
          <IconSearch
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <input
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
            onFocus={() => onOpenChange(true)}
            onKeyDown={(event) => {
              if (event.key === "Escape") onOpenChange(false);
              if (event.key === "Enter" && results[0]) {
                event.preventDefault();
                onSelect(results[0].code);
              }
            }}
            placeholder={placeholder}
            className="h-8 w-full rounded-md border border-slate-300 bg-white py-0 pr-[74px] pl-8 text-sm outline-none"
            aria-label={
              mode === "code"
                ? "Search tabular list by code"
                : "Search tabular list by description"
            }
          />
          <button
            type="button"
            aria-label="Reset tabular validation"
            title="Clear the search and the selected code"
            onClick={onReset}
            className="absolute right-1 top-1/2 inline-flex h-6 -translate-y-1/2 items-center gap-1 rounded border border-slate-300 px-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <IconRotate size={14} />
            Reset
          </button>
        </div>
      </header>
      {open && value.trim().length >= minLength ? (
        <div className="absolute top-full right-0 z-30 mt-1 max-h-[min(40rem,70vh)] w-[95%] overflow-auto rounded-md border border-sky-200 bg-sky-50 shadow-md">
          {loading && results.length === 0 ? (
            <p className="flex items-center gap-2 px-3 py-3 text-sm text-slate-600">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
              Searching tabular list…
            </p>
          ) : results.length === 0 ? (
            <p className="px-3 py-3 text-sm text-slate-600">
              No tabular codes match “{value.trim()}”.
            </p>
          ) : (
            <ul className="divide-y divide-sky-100">
              {results.map((hit) => {
                const isActive =
                  Boolean(activeCode) &&
                  stripIncompleteSuffix(hit.code) === activeCode;
                return (
                  <li key={hit.code}>
                    <button
                      type="button"
                      onClick={() => onSelect(hit.code)}
                      className={cn(
                        "grid w-full grid-cols-[92px_1fr] items-start gap-3 px-3 py-2 text-left transition-colors",
                        isActive ? "bg-sky-100 text-sky-950" : "hover:bg-sky-100"
                      )}
                    >
                      <span
                        className={cn(
                          "inline-flex h-6 w-fit items-center rounded-md border px-2 font-mono text-[13px] font-semibold",
                          hit.isPlaceholder
                            ? "border-dashed border-amber-400 bg-amber-50 text-amber-900"
                            : "border-sky-200 bg-sky-50 text-sky-950"
                        )}
                      >
                        {formatCmCode(hit.code)}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm leading-snug text-slate-900">
                          {hit.description}
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {[
                            hit.chapter ? `Chapter ${hit.chapter}` : "",
                            hit.sectionId,
                            hit.hasChildren ? "has more specific codes" : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};

export const TabularPanel: React.FC<{
  tabular: CmTabularResult | null;
  requestedCode: string | null;
  year: number;
  loading: boolean;
  selectedSeventh: string | null;
  onSelectSeventh: (char: string) => void;
  onOpenCode: (code: string) => void;
  finalizedCode: string | null;
  canCopyCode: boolean;
  canGoBack: boolean;
  onBack: () => void;
  onConfirmCode?: () => void;
}> = ({
  tabular,
  requestedCode,
  year,
  loading,
  selectedSeventh,
  onSelectSeventh,
  onOpenCode,
  finalizedCode,
  canCopyCode,
  canGoBack,
  onBack,
  onConfirmCode,
}) => {
  const [tabsExpanded, setTabsExpanded] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<string | undefined>(
    undefined
  );
  const tabCodeKey = `${requestedCode ?? ""}:${tabular?.code ?? ""}`;
  const [tabForCode, setTabForCode] = React.useState(tabCodeKey);
  if (tabForCode !== tabCodeKey) {
    setTabForCode(tabCodeKey);
    setActiveTab(undefined);
  }

  if (loading) {
    return (
      <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 px-4 py-8 text-center">
        <Spinner />
        <p className="text-[15px] font-medium text-slate-900">
          Loading tabular entry…
        </p>
        {requestedCode ? (
          <p className="font-mono text-sm text-slate-600">
            {formatCmCode(requestedCode)}
          </p>
        ) : null}
      </div>
    );
  }

  if (!tabular) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center">
        {requestedCode ? (
          <CmEmptyState
            title={`${formatCmCode(requestedCode)} is not in the FY ${year} Tabular List`}
            hint="The index entry points at a category that needs more characters. Open the nearest complete code from the index instead."
          />
        ) : (
          <CmEmptyState
            title="No code selected"
            hint="Pick a code from the index or a reference table to validate it against the Tabular List."
          />
        )}
      </div>
    );
  }

  const needsMoreDetail = tabular.children.length > 0;
  const inheritedGuidance = tabular.ancestors.filter((ancestor) =>
    notesHaveContent(ancestor.notes)
  );
  const ownNotesCount = CM_NOTE_SECTIONS.reduce(
    (total, section) => total + tabular.notes[section.key].length,
    0
  );
  const hasSeventhChars = tabular.seventhChars.length > 0;
  const preferSeventhTab = !tabular.isBillable && hasSeventhChars;

  let headerCode = tabular.code;
  if (tabular.needsSeventhChar && selectedSeventh && finalizedCode) {
    headerCode = finalizedCode;
  } else if (
    !tabular.isBillable &&
    requestedCode &&
    stripIncompleteSuffix(requestedCode) === tabular.code
  ) {
    headerCode = displayIncompleteCmCode(requestedCode, true);
  }

  const sections: Array<{ value: string; title: string; content: React.ReactNode }> =
    [];

  if (hasSeventhChars) {
    sections.push({
      value: "seventh",
      title: "7th character",
      content: (
        <>
          <div className="divide-y overflow-hidden">
            {tabular.seventhChars.map((opt) => (
              <button
                key={opt.char}
                type="button"
                onClick={() => onSelectSeventh(opt.char)}
                className={cn(
                  "grid w-full grid-cols-[28px_1fr] items-start gap-3 px-2 py-1.5 text-left text-[13px] transition-colors",
                  selectedSeventh === opt.char
                    ? "bg-slate-100 text-slate-900"
                    : "bg-white text-slate-800 hover:bg-slate-50"
                )}
              >
                <span
                  className={cn(
                    "inline-flex h-5 w-5 items-center justify-center rounded border font-mono font-semibold",
                    selectedSeventh === opt.char
                      ? "border-slate-800 bg-slate-900 text-white"
                      : "border-slate-300 bg-slate-100 text-slate-800"
                  )}
                >
                  {opt.char}
                </span>
                <span className="leading-relaxed">{opt.meaning}</span>
              </button>
            ))}
          </div>
          {selectedSeventh && finalizedCode ? (
            <p className="ml-2 mt-2 flex items-center gap-2 text-[13px] text-slate-600">
              Final code
              <span className="inline-flex h-6 items-center rounded-md border border-slate-300 bg-slate-100 px-2 font-mono text-[13px] font-semibold text-slate-900">
                {finalizedCode}
              </span>
            </p>
          ) : null}
        </>
      ),
    });
  }

  if (needsMoreDetail) {
    sections.push({
      value: "children",
      title: "More specific codes",
      content: (
        <ul className="divide-y overflow-hidden">
          {tabular.children.map((child) => (
            <CodeRow
              key={child.code}
              code={formatCmCode(child.code)}
              description={child.description}
              note={child.hasChildren ? "more levels" : ""}
              onOpen={() => onOpenCode(child.code)}
            />
          ))}
        </ul>
      ),
    });
  }

  if (ownNotesCount > 0) {
    sections.push({
      value: "notes",
      title: "Instructional notes",
      content: <NoteGroups notes={tabular.notes} />,
    });
  }

  if (inheritedGuidance.length > 0) {
    sections.push({
      value: "inherited",
      title: "Inherited guidance",
      content: (
        <div className="space-y-3">
          {inheritedGuidance.map((ancestor) => (
            <div key={ancestor.code} className="space-y-2 p-1">
              <div className="flex items-start gap-2">
                <CodeBadgeButton
                  code={ancestor.code}
                  size="md"
                  onClick={() => onOpenCode(ancestor.code)}
                />
                <span className="min-w-0 pt-0.5 text-base leading-relaxed text-slate-800">
                  {ancestor.description}
                </span>
              </div>
              <NoteGroups notes={ancestor.notes} />
            </div>
          ))}
        </div>
      ),
    });
  }

  const resolvedTab =
    activeTab && sections.some((section) => section.value === activeTab)
      ? activeTab
      : preferSeventhTab
        ? "seventh"
        : needsMoreDetail
          ? "children"
          : sections[0]?.value;

  const tabButtons = (
    <div className="flex items-center gap-1 border-b bg-slate-50 pr-1">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5 p-0.5">
        {sections.map((section) => (
          <button
            key={section.value}
            type="button"
            onClick={() => setActiveTab(section.value)}
            className={cn(
              "flex-none rounded-md border px-2 py-1 text-xs font-semibold uppercase tracking-wide text-blue-900",
              resolvedTab === section.value
                ? "border-sky-300 bg-sky-100 text-blue-950 shadow-sm"
                : "border-transparent hover:bg-sky-50"
            )}
          >
            {section.title}
          </button>
        ))}
      </div>
      {sections.length > 0 ? (
        <button
          type="button"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          aria-label="Expand tabular sections"
          title="Expand"
          onClick={() => {
            setActiveTab(resolvedTab);
            setTabsExpanded(true);
          }}
        >
          <IconMaximize size={14} />
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <section className="sticky top-0 z-10 border-b bg-white px-2 py-3">
        <div className="flex items-start gap-2">
          <button
            type="button"
            className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 disabled:opacity-40"
            aria-label="Back to previous code"
            title="Back to previous code"
            disabled={!canGoBack}
            onClick={onBack}
          >
            <IconArrowLeft size={14} />
          </button>
          <span
            className={cn(
              "inline-flex h-10 shrink-0 items-center rounded-md border px-3 font-mono text-xl font-bold leading-none",
              canCopyCode
                ? "border-green-500 bg-green-100 text-green-900"
                : "border-sky-200 bg-sky-50 text-sky-950"
            )}
          >
            {headerCode}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-medium leading-relaxed text-slate-900">
              {tabular.description}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {canCopyCode ? (
                <>
                  <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-md border border-teal-300 bg-teal-50 px-1.5 text-[11px] font-semibold uppercase tracking-wide text-teal-800">
                    <IconCheck size={12} />
                    valid
                  </span>
                  <ValidCodeLabelBadges
                    code={finalizedCode ?? headerCode}
                    year={year}
                  />
                  {onConfirmCode ? (
                    <button
                      type="button"
                      className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-green-600 px-2.5 text-sm text-white hover:bg-green-700"
                      onClick={onConfirmCode}
                    >
                      <IconPlus size={14} />
                      Add Code
                    </button>
                  ) : null}
                </>
              ) : (
                <>
                  <span className="inline-flex h-5 shrink-0 items-center rounded-md border border-amber-300 bg-amber-50 px-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                    incomplete
                  </span>
                  {tabular.isPlaceholder ? (
                    <StatusChip label="placeholder" />
                  ) : null}
                  {needsMoreDetail ? (
                    <StatusChip label="needs a more specific code" />
                  ) : null}
                  {tabular.needsSeventhChar ? (
                    <StatusChip label="7th character required" />
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {sections.length > 0 ? (
        <>
          {tabButtons}
          <div className="min-h-0 flex-1 overflow-auto bg-slate-50/40 p-0">
            {sections.map((section) =>
              section.value === resolvedTab ? (
                <div key={section.value}>{section.content}</div>
              ) : null
            )}
          </div>
        </>
      ) : null}

      {tabsExpanded ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="flex h-[92vh] w-[min(96vw,80rem)] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b px-6 py-3">
              <h2 className="min-w-0 flex-1 text-left font-semibold leading-snug text-green-800">
                <span className="font-mono text-xl tracking-wide">
                  {headerCode}
                </span>
                {tabular.description ? (
                  <span className="ml-2 text-sm font-medium text-green-700">
                    - {tabular.description}
                  </span>
                ) : null}
              </h2>
              <div className="flex shrink-0 items-center gap-2">
                <div className="flex items-center gap-0.5">
                  {sections.map((section) => (
                    <button
                      key={section.value}
                      type="button"
                      onClick={() => setActiveTab(section.value)}
                      className={cn(
                        "rounded-md border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide",
                        resolvedTab === section.value
                          ? "border-sky-300 bg-sky-100 text-blue-950"
                          : "border-transparent text-blue-900 hover:bg-sky-50"
                      )}
                    >
                      {section.title}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="h-8 rounded-md border border-slate-300 px-3 text-sm"
                  onClick={() => setTabsExpanded(false)}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-4">
              {sections.map((section) =>
                section.value === resolvedTab ? (
                  <div key={section.value}>{section.content}</div>
                ) : null
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
